/**
 * Pure aggregation helpers over the event stream of one demo session.
 * Each function takes the merged events list (session-scoped +
 * context-window) returned by GET /devices/:id/sessions/:sessionId and
 * derives one section's view-model. No React, no JSX — call sites wrap
 * these in useMemo so the page re-derives only on data change.
 *
 * Keep the helpers ts-pure and single-pass per function so they remain
 * cheap even on long sessions (hundreds of events).
 */
import type { EventRow } from '../api/hooks';

// Mirrors backend/src/routes/analytics.ts IMPRESSION_THRESHOLD_MS (2000).
// Per-product `product_view` events from the carousel with dwell < 2 s
// are counted as "sweeps" (the user paged past, not a deliberate look).
// Keep the two definitions in sync.
const IMPRESSION_THRESHOLD_MS = 2000;

// ─── Product engagement ────────────────────────────────────────────────

export interface ProductDetailView {
  offset_ms: number;
  dwell_ms: number;
}
export interface ProductFeatureOpen {
  offset_ms: number;
  dwell_ms: number;
  pill_id: string;
  pill_label: string;
}
export interface ProductGalleryOpen {
  offset_ms: number;
  dwell_ms: number;
  gallery_index: number;
}

export interface ProductSessionAgg {
  product_model: string;
  impressions: number;       // carousel product_view, dwell >= 2 s
  sweeps: number;            // carousel product_view, dwell < 2 s
  detail_views: number;      // product_view source='detail'
  total_detail_dwell_ms: number;
  avg_detail_dwell_ms: number;
  popups: number;            // feature_view count
  gallery: number;           // gallery_view count
  // Drill-down ammunition: the per-product event lists used to render
  // the expanded panel beneath an accordion row. Each list is sorted
  // ts-ASC because the input stream already is.
  carousel_impressions_dwell_ms: number;
  carousel_sweep_dwell_ms: number;
  detail_events: ProductDetailView[];
  feature_events: ProductFeatureOpen[];
  gallery_events: ProductGalleryOpen[];
}

export interface CompareAgg {
  views: number;
  total_dwell_ms: number;
}

export function aggregateProducts(
  events: EventRow[],
  startMs: number,
): {
  perProduct: ProductSessionAgg[];
  compare: CompareAgg;
} {
  const map = new Map<string, ProductSessionAgg>();
  const compare: CompareAgg = { views: 0, total_dwell_ms: 0 };
  const ensure = (model: string): ProductSessionAgg => {
    let agg = map.get(model);
    if (!agg) {
      agg = {
        product_model: model,
        impressions: 0,
        sweeps: 0,
        detail_views: 0,
        total_detail_dwell_ms: 0,
        avg_detail_dwell_ms: 0,
        popups: 0,
        gallery: 0,
        carousel_impressions_dwell_ms: 0,
        carousel_sweep_dwell_ms: 0,
        detail_events: [],
        feature_events: [],
        gallery_events: [],
      };
      map.set(model, agg);
    }
    return agg;
  };
  const offsetFor = (ts: string) =>
    Math.max(0, new Date(ts).getTime() - startMs);
  for (const ev of events) {
    const d = (ev.data ?? {}) as Record<string, unknown>;
    if (ev.event_type === 'product_view') {
      const model = typeof d.product_model === 'string' ? d.product_model : null;
      if (!model) continue;
      const source = d.source;
      const dwell = Number(d.dwell_ms ?? 0);
      const agg = ensure(model);
      if (source === 'carousel') {
        if (dwell >= IMPRESSION_THRESHOLD_MS) {
          agg.impressions += 1;
          agg.carousel_impressions_dwell_ms += dwell;
        } else {
          agg.sweeps += 1;
          agg.carousel_sweep_dwell_ms += dwell;
        }
      } else if (source === 'detail') {
        agg.detail_views += 1;
        agg.total_detail_dwell_ms += dwell;
        agg.detail_events.push({
          offset_ms: offsetFor(ev.ts),
          dwell_ms: dwell,
        });
      }
    } else if (ev.event_type === 'feature_view') {
      const model = typeof d.product_model === 'string' ? d.product_model : null;
      if (!model) continue;
      const agg = ensure(model);
      agg.popups += 1;
      agg.feature_events.push({
        offset_ms: offsetFor(ev.ts),
        dwell_ms: Number(d.dwell_ms ?? 0),
        pill_id: typeof d.pill_id === 'string' ? d.pill_id : '',
        pill_label:
          typeof d.pill_label === 'string'
            ? d.pill_label
            : typeof d.pill_id === 'string'
              ? d.pill_id
              : '?',
      });
    } else if (ev.event_type === 'gallery_view') {
      const model = typeof d.product_model === 'string' ? d.product_model : null;
      if (!model) continue;
      const agg = ensure(model);
      agg.gallery += 1;
      const idx = Number(d.gallery_index ?? 0);
      agg.gallery_events.push({
        offset_ms: offsetFor(ev.ts),
        dwell_ms: Number(d.dwell_ms ?? 0),
        gallery_index: Number.isFinite(idx) ? idx : 0,
      });
    } else if (ev.event_type === 'compare_view') {
      compare.views += 1;
      compare.total_dwell_ms += Number(d.dwell_ms ?? 0);
    }
  }
  // Finalise averages.
  for (const agg of map.values()) {
    agg.avg_detail_dwell_ms =
      agg.detail_views > 0
        ? Math.round(agg.total_detail_dwell_ms / agg.detail_views)
        : 0;
  }
  // Sort by engagement intensity, descending. Same secondary key as the
  // backend's per-device product list so a user comparing the two reads
  // a consistent ordering.
  const perProduct = Array.from(map.values()).sort((a, b) => {
    if (b.detail_views !== a.detail_views) return b.detail_views - a.detail_views;
    if (b.impressions !== a.impressions) return b.impressions - a.impressions;
    return a.product_model.localeCompare(b.product_model);
  });
  return { perProduct, compare };
}

// ─── View visits ───────────────────────────────────────────────────────

export interface ViewAgg {
  view: string;
  visits: number;
  total_dwell_ms: number;
  /** "down×3 ok×1" — keys map flattened, sorted by count desc. */
  keys_summary: string;
}

export function aggregateViews(events: EventRow[]): ViewAgg[] {
  const map = new Map<string, {
    view: string;
    visits: number;
    total_dwell_ms: number;
    keys: Map<string, number>;
  }>();
  for (const ev of events) {
    if (ev.event_type !== 'view_visit') continue;
    const d = (ev.data ?? {}) as Record<string, unknown>;
    const view = typeof d.view === 'string' ? d.view : '?';
    let agg = map.get(view);
    if (!agg) {
      agg = { view, visits: 0, total_dwell_ms: 0, keys: new Map() };
      map.set(view, agg);
    }
    agg.visits += 1;
    agg.total_dwell_ms += Number(d.dwell_ms ?? 0);
    const keys = d.keys as Record<string, unknown> | undefined;
    if (keys && typeof keys === 'object') {
      for (const [k, v] of Object.entries(keys)) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) {
          agg.keys.set(k, (agg.keys.get(k) ?? 0) + n);
        }
      }
    }
  }
  const out: ViewAgg[] = [];
  for (const a of map.values()) {
    const sorted = Array.from(a.keys.entries()).sort((x, y) => y[1] - x[1]);
    out.push({
      view: a.view,
      visits: a.visits,
      total_dwell_ms: a.total_dwell_ms,
      keys_summary: sorted.map(([k, n]) => `${k}×${n}`).join(' '),
    });
  }
  // Descending by time spent — most-engaged views surface first.
  out.sort((a, b) => b.total_dwell_ms - a.total_dwell_ms);
  return out;
}

// ─── Content played ────────────────────────────────────────────────────

export interface ContentPlay {
  event_id: string;
  ts: string;
  id: string;
  kind: string;
  watched_ms: number;
  completed: boolean;
}

export function aggregateContent(events: EventRow[]): {
  plays: ContentPlay[];
  totalWatchedMs: number;
} {
  const plays: ContentPlay[] = [];
  let totalWatchedMs = 0;
  for (const ev of events) {
    if (ev.event_type !== 'content_play') continue;
    const d = (ev.data ?? {}) as Record<string, unknown>;
    const watched = Number(d.watched_ms ?? 0);
    plays.push({
      event_id: ev.event_id,
      ts: ev.ts,
      id: typeof d.id === 'string' ? d.id : '?',
      kind: typeof d.kind === 'string' ? d.kind : 'video',
      watched_ms: watched,
      completed: d.completed === true,
    });
    totalWatchedMs += watched;
  }
  return { plays, totalWatchedMs };
}

// ─── Source switches ───────────────────────────────────────────────────

export interface SourceSwitch {
  ts: number;             // unix ms
  offset_ms: number;      // ms since session start
  from: string | null;
  to: string;
  dwell_s: number;        // dwell on `from` before switching
}

export function aggregateSourceSwitches(
  events: EventRow[],
  startMs: number,
): SourceSwitch[] {
  const out: SourceSwitch[] = [];
  for (const ev of events) {
    if (ev.event_type !== 'source_dwell') continue;
    const d = (ev.data ?? {}) as Record<string, unknown>;
    const ts = new Date(ev.ts).getTime();
    out.push({
      ts,
      offset_ms: Math.max(0, ts - startMs),
      from: typeof d.from === 'string' ? d.from : null,
      to: typeof d.to === 'string' ? d.to : '?',
      dwell_s: Number(d.dwell_s ?? 0),
    });
  }
  return out;
}

// ─── Feedback ──────────────────────────────────────────────────────────

export function findFeedbackRatings(events: EventRow[]): number[] | null {
  for (const ev of events) {
    if (ev.event_type !== 'feedback') continue;
    const d = (ev.data ?? {}) as Record<string, unknown>;
    const ratings = d.ratings;
    if (Array.isArray(ratings)) {
      const nums = ratings
        .map((r) => Number(r))
        .filter((n) => Number.isFinite(n));
      if (nums.length > 0) return nums;
    }
    return null;
  }
  return null;
}

// ─── Errors ────────────────────────────────────────────────────────────

export interface SessionError {
  event_id: string;
  ts: string;
  offset_ms: number;
  message: string;
}

export function findErrors(
  events: EventRow[],
  startMs: number,
  endMs: number,
): SessionError[] {
  const out: SessionError[] = [];
  for (const ev of events) {
    if (ev.event_type !== 'error') continue;
    const ts = new Date(ev.ts).getTime();
    if (ts < startMs || ts > endMs) continue;
    const d = (ev.data ?? {}) as Record<string, unknown>;
    const message = String(d.message ?? d.code ?? 'error');
    out.push({
      event_id: ev.event_id,
      ts: ev.ts,
      offset_ms: Math.max(0, ts - startMs),
      message,
    });
  }
  return out;
}
